import { forwardRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import styled from 'styled-components';
import UIIcon from 'UIComponents/icon/UIIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITooltip from 'UIComponents/tooltip/UITooltip';
// @ts-expect-error module not typed
import { getInReplyToId } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getCommonMessage } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { getForwardCommunicatorState } from 'composer-data/email/public/selectors';
// TODO: move to composer-ui
import { getFocusedThreadVisitor } from 'conversations-thread-view/visitor/selectors/getFocusedThreadVisitor';
import { getRecipientsIconName } from '../operators/getRecipientsIconName';
import DeliveryIdentifierSearchInput from './DeliveryIdentifierSearchInput';
import MetadataInputLabel from './MetadataInputLabel';
import { RecipientTooltipTitle } from './RecipientTooltipTitle';
import GenericRecipientSearchInput from './GenericRecipientSearchInput';
import { getAuthAsPojo } from 'conversations-thread-data/auth/public/selectors';
import { useNeverLog } from '../../../email/protected/hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FieldWrapper = styled.label.withConfig({
  displayName: "RecipientField__FieldWrapper",
  componentId: "sc-5op943-0"
})(["display:inline-flex;flex-grow:1;width:90%;"]);
const noop = () => {};
function RecipientFieldLabel({
  fieldName
}) {
  switch (fieldName) {
    case 'CC':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.metadata-editor.recipient-field.label.cc"
      });
    case 'BCC':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.metadata-editor.recipient-field.label.bcc"
      });
    case 'TO':
    default:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.metadata-editor.recipient-field.label.to"
      });
  }
}
RecipientFieldLabel.displayName = 'RecipientFieldLabel';
const _RecipientField = (props, ref) => {
  const {
    allRecipients = ImmutableMap(),
    delayedExpanded = true,
    deliveryIdentifier,
    fieldName,
    handleSetExpanded = noop,
    index,
    isComposer,
    recipientFieldsExpansionState,
    recipientKey,
    reply,
    subfieldAutofocus = false,
    valueChangeHandler = noop,
    toLabel,
    useWhatsAppCompose
  } = props;
  const {
    replyAll
  } = useSelector(getForwardCommunicatorState);
  const defaultRecipient = useSelector(getFocusedThreadVisitor);
  const auth = useSelector(getAuthAsPojo);
  const scopes = auth && auth.user ? auth.user.scopes : [];
  const commonMessageReply = getCommonMessage(reply);
  const inReplyToId = getInReplyToId(commonMessageReply);
  const iconName = getRecipientsIconName({
    allRecipients,
    commonMessageReply,
    replyAll
  });
  const {
    emailBlocklistStatus
  } = useNeverLog({
    commonMessage: commonMessageReply
  });
  const value = recipientKey ? allRecipients.get(recipientKey) : ImmutableList();
  const [relatedRecipients] = useState(() => value);
  const hasWhatsAppAccess = scopes.includes('messaging-access') && scopes.includes('messaging-trusted');
  const searchInput = useWhatsAppCompose ? /*#__PURE__*/_jsx(GenericRecipientSearchInput, {
    contactProperty: hasWhatsAppAccess ? deliveryIdentifier : 'email',
    autoFocus: index === 0 ? true : subfieldAutofocus,
    clearableValues: true,
    value: value,
    onFocus: index === 0 ? handleSetExpanded : noop,
    onChange: valueChangeHandler,
    defaultRecipient: index === 0 && !isComposer ? defaultRecipient : null,
    relatedRecipients: relatedRecipients,
    emailBlocklistStatus: emailBlocklistStatus,
    index: index,
    ref: ref
  }) : /*#__PURE__*/_jsx(DeliveryIdentifierSearchInput, {
    deliveryIdentifier: deliveryIdentifier,
    autoFocus: index === 0 ? true : subfieldAutofocus,
    clearableValues: true,
    value: value,
    onFocus: index === 0 ? handleSetExpanded : noop,
    onChange: valueChangeHandler,
    defaultRecipient: index === 0 && !isComposer ? defaultRecipient : null,
    relatedRecipients: relatedRecipients,
    emailBlocklistStatus: emailBlocklistStatus,
    index: index,
    ref: ref
  });
  const label = index === 0 ? toLabel : /*#__PURE__*/_jsx(RecipientFieldLabel, {
    fieldName: fieldName
  });
  if (inReplyToId) {
    return index === 0 ? /*#__PURE__*/_jsxs(FieldWrapper, {
      "data-selenium-test": `metadata-${recipientKey}-input`,
      children: [/*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(RecipientTooltipTitle, {
          iconName: iconName
        }),
        children: /*#__PURE__*/_jsx(UIIcon, {
          "data-selenium-test": `metadata-${recipientKey}-icon`,
          name: iconName,
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            marginRight: 8,
            marginTop: 4
          },
          size: 24
        })
      }), searchInput]
    }) : /*#__PURE__*/_jsx(MetadataInputLabel, {
      label: label,
      "data-selenium-test": `metadata-${recipientKey}-input`,
      children: searchInput
    });
  }
  const shouldDisplaySearchInput = delayedExpanded && (recipientFieldsExpansionState.get(index) || index === 0);
  if (!shouldDisplaySearchInput) {
    return null;
  }
  return /*#__PURE__*/_jsx(MetadataInputLabel, {
    label: label,
    "data-selenium-test": `metadata-${recipientKey}-input`,
    children: searchInput
  });
};
_RecipientField.displayName = '_RecipientField';
const RecipientField = /*#__PURE__*/forwardRef(_RecipientField);
export default RecipientField;